// Brief Prospection Phase 1 §3 — Panneau de partage universel (expert-forward).
// Copier le lien OG (aperçus WhatsApp/LinkedIn garantis), QR code, boutons
// sociaux avec message pré-rédigé éditable.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { X, Copy, Check, Mail, Linkedin, MessageCircle, BarChart3 } from 'lucide-react';

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const SharePanel = ({
  isOpen,
  onClose,
  title = 'Partager',
  shareUrl,       // URL OG (aperçus garantis) — celle qu'on diffuse
  prettyUrl,      // URL propre affichée à titre indicatif
  defaultMessage = '',
}) => {
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState(defaultMessage);

  if (!isOpen) return null;

  // Brief Prospection Phase 2 — attribution par canal (?ref=)
  const withRef = (ref) => `${shareUrl}${shareUrl.includes('?') ? '&' : '?'}ref=${ref}`;
  const fullText = (ref) => (message ? `${message}\n${withRef(ref)}` : withRef(ref));

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };

  const openPopup = (url) => window.open(url, '_blank', 'width=620,height=460');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="share-panel-overlay"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid="share-panel-close"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Lien */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Votre lien public</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 truncate" data-testid="share-panel-url">
                {prettyUrl || shareUrl}
              </div>
              <button
                onClick={() => copy(withRef('copy'))}
                className={`px-3 py-2 rounded-xl text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${
                  copied ? 'bg-emerald-100 text-emerald-700' : 'text-white hover:opacity-90'
                }`}
                style={copied ? {} : { backgroundColor: '#7F77DD' }}
                data-testid="share-panel-copy"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copié' : 'Copier'}
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">
              Ce lien génère un aperçu riche sur WhatsApp, LinkedIn et les autres réseaux.
            </p>
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-2 py-2">
            <div className="p-3 bg-white rounded-2xl border border-gray-200 shadow-sm" data-testid="share-panel-qr">
              <QRCodeSVG value={withRef('qr')} size={150} fgColor="#1f2937" />
            </div>
            <p className="text-xs text-gray-400">Scannez pour ouvrir le lien</p>
          </div>

          {/* Message pré-rédigé */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Message pré-rédigé</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40 resize-none"
              data-testid="share-panel-message"
            />
          </div>

          {/* Boutons sociaux */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => openPopup(`https://wa.me/?text=${encodeURIComponent(fullText('wa'))}`)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#25D366' }}
              data-testid="share-panel-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </button>
            <button
              onClick={() => openPopup(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withRef('li'))}`)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#0A66C2' }}
              data-testid="share-panel-linkedin"
            >
              <Linkedin className="h-4 w-4" /> LinkedIn
            </button>
            <button
              onClick={() => openPopup(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(withRef('x'))}`)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-white bg-gray-900 hover:opacity-90 transition-opacity"
              data-testid="share-panel-x"
            >
              <XIcon /> X
            </button>
            <a
              href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(fullText('mail'))}`}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              data-testid="share-panel-email"
            >
              <Mail className="h-4 w-4" /> Email
            </a>
          </div>

          <button
            onClick={() => copy(fullText('copy'))}
            className="w-full px-3 py-2.5 rounded-xl text-sm font-semibold text-[#7F77DD] border border-[#7F77DD]/40 hover:bg-[#7F77DD]/5 transition-colors"
            data-testid="share-panel-copy-message"
          >
            Copier message + lien
          </button>

          {/* Brief Prospection Phase 2 — accès au dashboard d'attribution */}
          <Link
            to="/app/sessions?tab=prospection"
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-[#7F77DD] transition-colors"
            data-testid="share-panel-stats-link"
          >
            <BarChart3 className="h-3.5 w-3.5" /> Voir mes statistiques de partage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SharePanel;
